/* global globalThis */
/* eslint-disable max-len */

import { generateAsyncRoute } from '@thd-olt-component-react/router';

const ssr = globalThis.window === undefined || import.meta.env.SSR;

const resolveComponent = ({
  routeName,
  componentName,
  serverPage,
  chunk,
  currentRouteName,
  currentRouteComponent
}) => {
  if (ssr) {
    return serverPage;
  }

  if (currentRouteName === routeName) {
    return currentRouteComponent;
  }

  return generateAsyncRoute(() => chunk().then((pageModule) => ({ default: pageModule[componentName] })));
};

const createRoute = ({
  routeName,
  componentName,
  path,
  chunk,
  serverPage,
  currentRouteName,
  currentRouteComponent
}) => ({
  name: routeName,
  componentName,
  chunk,
  get component() {
    return resolveComponent({
      routeName,
      componentName,
      serverPage,
      chunk,
      currentRouteName,
      currentRouteComponent
    });
  },
  path,
  channel: null
});

export const getServerPages = async () => {
  const Components = {};
  if (ssr) {
    Components.reservation = (await import('./pages/Reservation')).Reservation;
    Components.delivery = (await import('./pages/Delivery')).Delivery;
    Components.clearCallback = (await import('./pages/ClearCallback')).ClearCallback;
    Components.clearVerification = (await import('./pages/ClearVerification')).ClearVerification;
    Components.clearCallbackV2 = (await import('./pages/ClearCallbackV2')).ClearCallbackV2;
    Components.selfDropoff = (await import('./pages/SelfDropoff')).SelfDropoff;
    Components.reservationDetails = (await import('./pages/ReservationDetails')).ReservationDetails;
    Components.manageReservation = (await import('./pages/ManageReservation')).ManageReservation;
    Components.manageReservationV2 = (await import('./pages/ManageReservationV2')).ManageReservationV2;
    Components.cancelReservation = (await import('./pages/CancelReservation')).CancelReservation;
    Components.product = (await import('./pages/Product')).Product;
    Components.usedEquipment = (await import('./pages/UsedEquipment')).UsedEquipment;
  }
  return Components;
};

export const getRoutes = (currentRouteName, currentRouteComponent, serverPages) => {
  const normalizedServerPages = serverPages || {};

  return ({
    reservation: createRoute({
      routeName: 'Reservation',
      componentName: 'Reservation',
      path: '/:context?/p/rental/reservations/:slug?/:itemId',
      chunk: () => import('./pages/Reservation'),
      serverPage: normalizedServerPages.reservation,
      currentRouteName,
      currentRouteComponent
    }),
    delivery: createRoute({
      routeName: 'Delivery',
      componentName: 'Delivery',
      path: '/:context?/p/rental/delivery/:slug?/:itemId',
      chunk: () => import('./pages/Delivery'),
      serverPage: normalizedServerPages.delivery,
      currentRouteName,
      currentRouteComponent
    }),
    clearCallback: createRoute({
      routeName: 'ClearCallback',
      componentName: 'ClearCallback',
      path: '/:context?/rental/clear-verification/callback',
      chunk: () => import('./pages/ClearCallback'),
      serverPage: normalizedServerPages.clearCallback,
      currentRouteName,
      currentRouteComponent
    }),
    clearVerification: createRoute({
      routeName: 'ClearVerification',
      componentName: 'ClearVerification',
      path: '/:context?/rental/clear-verification/:clearState',
      chunk: () => import('./pages/ClearVerification'),
      serverPage: normalizedServerPages.clearVerification,
      currentRouteName,
      currentRouteComponent
    }),
    clearCallbackV2: createRoute({
      routeName: 'ClearCallbackV2',
      componentName: 'ClearCallbackV2',
      path: '/:context?/rental/clear-verification/v2/callback/:uuid',
      chunk: () => import('./pages/ClearCallbackV2'),
      serverPage: normalizedServerPages.clearCallbackV2,
      currentRouteName,
      currentRouteComponent
    }),
    selfDropoff: createRoute({
      routeName: 'SelfDropoff',
      componentName: 'SelfDropoff',
      path: '/:context?/rental/self-dropoff/:storeId',
      chunk: () => import('./pages/SelfDropoff'),
      serverPage: normalizedServerPages.selfDropoff,
      currentRouteName,
      currentRouteComponent
    }),
    // TODO: Remove once transitioned off
    reservationDetails: createRoute({
      routeName: 'ReservationDetails',
      componentName: 'ReservationDetails',
      path: '/:context?/p/rental/reservationDetails',
      chunk: () => import('./pages/ReservationDetails'),
      serverPage: normalizedServerPages.reservationDetails,
      currentRouteName,
      currentRouteComponent
    }),
    manageReservation: createRoute({
      routeName: 'ManageReservation',
      componentName: 'ManageReservation',
      path: '/:context?/rental/manage-reservation/:uuid/:reservationId/:storeId',
      chunk: () => import('./pages/ManageReservation'),
      serverPage: normalizedServerPages.manageReservation,
      currentRouteName,
      currentRouteComponent
    }),
    manageReservationV2: createRoute({
      routeName: 'ManageReservationV2',
      componentName: 'ManageReservationV2',
      path: '/:context?/rental/manage-reservation/v2/:uuid/:reservationId/:storeId',
      chunk: () => import('./pages/ManageReservationV2'),
      serverPage: normalizedServerPages.manageReservationV2,
      currentRouteName,
      currentRouteComponent
    }),
    cancelReservation: createRoute({
      routeName: 'CancelReservation',
      componentName: 'CancelReservation',
      path: '/:context?/rental/cancel-reservation/:cancellationId/:reservationNumber',
      chunk: () => import('./pages/CancelReservation'),
      serverPage: normalizedServerPages.cancelReservation,
      currentRouteName,
      currentRouteComponent
    }),
    product: createRoute({
      routeName: 'Product',
      componentName: 'Product',
      path: '/:context?/p/rental/:slug?/:itemId',
      chunk: () => import('./pages/Product'),
      serverPage: normalizedServerPages.product,
      currentRouteName,
      currentRouteComponent
    }),
    usedEquipment: createRoute({
      routeName: 'UsedEquipment',
      componentName: 'UsedEquipment',
      path: '/:context?/rental/used-equipment',
      chunk: () => import('./pages/UsedEquipment'),
      serverPage: normalizedServerPages.usedEquipment,
      currentRouteName,
      currentRouteComponent
    })
  });
};

export const getRouteChunk = async (currentRouteName) => {
  const routes = getRoutes();
  const routeKey = Object.keys(routes).find((key) => routes[key].name === currentRouteName);
  const route = routes[routeKey];
  const componentModule = await route.chunk();

  return componentModule[route.componentName];
};
